/** Defaults for follow-up settings (shared by service + UI). */
import type { FollowUpDelayUnit } from './types';

/** Limites anti-ban — lógica em `_shared` (Edge + app). Presets abaixo são só UI. */
export {
  DEFAULT_MIN_INTERVAL_SECONDS,
  DEFAULT_MAX_SENDS_PER_CYCLE,
  MIN_INTERVAL_SECONDS_MIN,
  MIN_INTERVAL_SECONDS_MAX,
  MAX_SENDS_PER_CYCLE_MIN,
  MAX_SENDS_PER_CYCLE_MAX,
  clampMinIntervalSeconds,
  clampMaxSendsPerCycle,
} from '../../../supabase/functions/_shared/followUpAntiBan';

import {
  clampMaxSendsPerCycle,
  clampMinIntervalSeconds,
} from '../../../supabase/functions/_shared/followUpAntiBan';

export const MIN_INTERVAL_PRESETS = [45, 60, 90, 120, 180, 300] as const;
export const MAX_SENDS_PRESETS = [1, 3, 5, 8, 10] as const;

export function formatMinIntervalPresetLabel(seconds: number): string {
  if (seconds < 60) return `${seconds} segundos`;
  if (seconds === 60) return '1 minuto';
  if (seconds % 60 === 0) return `${seconds / 60} minutos`;
  return `${seconds} segundos`;
}

export const MIN_INTERVAL_PRESET_OPTIONS = MIN_INTERVAL_PRESETS.map((seconds) => ({
  value: String(seconds),
  label: formatMinIntervalPresetLabel(seconds),
}));

export const MAX_SENDS_PRESET_OPTIONS = MAX_SENDS_PRESETS.map((count) => ({
  value: String(count),
  label: count === 1 ? '1 envio' : `${count} envios`,
}));

function nearestPreset(value: number, presets: readonly number[]): number {
  return presets.reduce((best, preset) =>
    Math.abs(preset - value) < Math.abs(best - value) ? preset : best
  );
}

export function snapMinIntervalSeconds(value: number): number {
  return nearestPreset(clampMinIntervalSeconds(value), MIN_INTERVAL_PRESETS);
}

export function snapMaxSendsPerCycle(value: number): number {
  return nearestPreset(clampMaxSendsPerCycle(value), MAX_SENDS_PRESETS);
}

export const DEFAULT_PAUSE_TIMEOUT_HOURS = 24;
export const DEFAULT_FOLLOW_UP_TIMEZONE = 'America/Sao_Paulo';
export const DEFAULT_BUSINESS_HOURS_START = '09:00';
export const DEFAULT_BUSINESS_HOURS_END = '18:00';

export const FOLLOW_UP_TIMEZONE_OPTIONS = [
  { value: 'America/Sao_Paulo', label: 'Brasília (São Paulo)' },
  { value: 'America/Manaus', label: 'Manaus' },
  { value: 'America/Fortaleza', label: 'Fortaleza' },
  { value: 'America/Recife', label: 'Recife' },
  { value: 'UTC', label: 'UTC' },
] as const;

export const FOLLOW_UP_TIMEZONE_VALUES = new Set<string>(
  FOLLOW_UP_TIMEZONE_OPTIONS.map((option) => option.value)
);

/** Normalizes DB `time` (e.g. "09:00:00") or UI input to "HH:mm". */
export function sliceTimeToHHmm(value: string): string {
  return value.slice(0, 5);
}

export function isAllowedFollowUpTimezone(timezone: string): boolean {
  return FOLLOW_UP_TIMEZONE_VALUES.has(timezone);
}

export function isBusinessHoursRangeValid(start: string, end: string): boolean {
  return sliceTimeToHHmm(start) < sliceTimeToHHmm(end);
}

export const PAUSE_TIMEOUT_HOURS_MIN = 1;

export const FOLLOW_UP_DELAY_UNIT_OPTIONS = [
  { value: 'seconds', label: 'segundos' },
  { value: 'minutes', label: 'minutos' },
  { value: 'hours', label: 'horas' },
  { value: 'days', label: 'dias' },
] as const;

export const DEFAULT_FOLLOW_UP_DELAY = { delayValue: 30, delayUnit: 'seconds' as const };

export function formatDelayLabel(value: number, unit: FollowUpDelayUnit): string {
  if (unit === 'seconds') return value === 1 ? '1 segundo' : `${value} segundos`;
  if (unit === 'minutes') return value === 1 ? '1 minuto' : `${value} minutos`;
  if (unit === 'hours') return value === 1 ? '1 hora' : `${value} horas`;
  return value === 1 ? '1 dia' : `${value} dias`;
}

/** Motivo gravado em `followup_jobs.failure_reason` ao cancelar por ganho/perda. */
export const FOLLOW_UP_CANCEL_REASON_CLOSED = 'Lead em exclusão';
export const FOLLOW_UP_CANCEL_REASON_OUT_OF_STAGE = 'Fora dos estágios';

/**
 * Allowlist de etapas do funil.
 * `[]` = todas as etapas abertas (compatível com orgs sem filtro).
 * Ganho/perdido nunca passam por aqui — o caller recusa `status !== 'open'`.
 */
export function isFollowUpStageAllowed(
  stageId: string | null | undefined,
  allowedStageIds: readonly string[],
): boolean {
  if (!allowedStageIds.length) return true;
  if (!stageId) return false;
  return allowedStageIds.includes(stageId);
}

/** Status do histórico/KPI — uma fonte só para labels e tom visual. */
export const FOLLOW_UP_HISTORY_STATUSES = [
  { id: 'scheduled', label: 'Programados', accentClass: 'bg-primary', valueClass: 'text-primary', subtitle: 'Aguardando envio' },
  { id: 'paused', label: 'Pausados', accentClass: 'bg-warning', valueClass: 'text-warning', subtitle: 'Aguardando o lead' },
  { id: 'sent', label: 'Enviados', accentClass: 'bg-success', valueClass: 'text-success', subtitle: 'Já disparados' },
  { id: 'failed', label: 'Falhas', accentClass: 'bg-error', valueClass: 'text-error', subtitle: 'Requer atenção' },
] as const;

/** Chips do editor — inserem placeholders resolvidos no envio pelo motor. */
export const FOLLOW_UP_VARIABLE_CHIPS = [
  { label: 'Nome', tag: '{{nome_contato}}' },
  { label: 'Telefone', tag: '{{telefone}}' },
] as const;
